import { useCallback, useEffect, useRef, useState } from "react";
import { api, money, formatApiError } from "@/lib/api";
import { Plus, Trash2, Save, Copy, ListChecks, ArrowLeft, Clock, FileBadge, FileUp, Loader2, History, X } from "lucide-react";
import { toast } from "sonner";
import HelpTip from "@/components/HelpTip";
import { useAuth } from "@/contexts/AuthContext";

export default function PriceLists() {
    const { user } = useAuth();
    const [lists, setLists] = useState([]);
    const [active, setActive] = useState(null);   // lista actualmente editándose
    const [creating, setCreating] = useState(false);
    const [newList, setNewList] = useState({ name: "", description: "", source_list_id: "" });
    const [aiBusy, setAiBusy] = useState(false);
    const [historyModal, setHistoryModal] = useState(null); // {type, key, label} | null
    const fileInputRef = useRef(null);
    const isAdmin = user?.role === "superadmin" || user?.role === "direccion";

    const openHistory = useCallback((type, key, label) => {
        setHistoryModal({ type, key, label });
    }, []);

    const parsePdfWithAi = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".pdf")) {
            toast.error("Solo PDFs por ahora");
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }
        setAiBusy(true);
        const formData = new FormData();
        formData.append("file", file);
        formData.append("create_list", "true");
        try {
            const { data } = await api.post("/price-lists/parse-pdf", formData, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 90000,
            });
            toast.success(`Lista "${data.list_name}" creada con IA: ${data.items_count} servicios + ${data.concepts_count} conceptos`);
            setActive(data.list);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Error al parsear PDF");
        } finally {
            setAiBusy(false);
            // Reset el input para permitir resubir el mismo archivo
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const load = useCallback(async (updatedList) => {
        try {
            const { data } = await api.get("/price-lists");
            setLists(data || []);
            // Si el editor pasó la versión actualizada de la lista activa, reflectarla.
            // Si no, intentamos reseatear active con la versión más fresca del array.
            setActive((prev) => {
                if (updatedList && prev && updatedList.id === prev.id) return updatedList;
                if (prev) {
                    const fresh = (data || []).find((l) => l.id === prev.id);
                    return fresh || prev;
                }
                return (data && data[0]) || null;
            });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "No se pudieron cargar las listas");
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const updateBasic = async () => {
        if (!active) return;
        try {
            await api.patch(`/price-lists/${active.id}`, {
                name: active.name,
                description: active.description,
            });
            toast.success("Lista guardada");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error al guardar");
        }
    };

    const copyOfficial = async (list) => {
        if (!list) return;
        const confirm = window.confirm(
            `Copiar los 22 conceptos oficiales del Tarifario Público a "${list.name}".\n\n` +
            `• Si esta lista ya tiene conceptos: se AGREGAN solo los códigos que no existan (no sobrescribe los tuyos).\n` +
            `• Si está vacía: queda con los 22 oficiales listos para ajustar precios.\n\nContinuar?`
        );
        if (!confirm) return;
        try {
            const { data } = await api.post(`/price-lists/${list.id}/copy-from-official`, { replace: false });
            toast.success(`Tarifario copiado: ${data.appended || 0} concepto(s) agregado(s) (total: ${data.items_total}).`);
            setActive(data.list);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error al copiar tarifario");
        }
    };

    const create = async () => {
        if (!newList.name.trim()) return toast.error("Captura un nombre");
        try {
            const { data } = await api.post("/price-lists", newList);
            toast.success(`Lista "${data.name}" creada`);
            setNewList({ name: "", description: "", source_list_id: "" });
            setCreating(false);
            setActive(data);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error al crear");
        }
    };

    const remove = async (list) => {
        if (!window.confirm(`¿Eliminar la lista "${list.name}"? Esto NO afecta servicios ya cotizados, solo el catálogo.`)) return;
        try {
            await api.delete(`/price-lists/${list.id}`);
            toast.success("Lista eliminada");
            setActive(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error al eliminar");
        }
    };

    return (
        <div className="space-y-4" data-testid="price-lists-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
                        <ListChecks className="w-6 h-6 text-red-600" /> Listas de Precios
                        <HelpTip text="Catálogos tarifarios reutilizables. La lista 'Público' es la base del sistema. Puedes crear listas adicionales para aseguradoras, hospitales, escuelas, etc. y asignarlas a tus clientes corporativos para que el cotizador aplique automáticamente las tarifas correctas." variant="?" />
                    </h2>
                    <div className="text-xs text-slate-500 font-mono">
                        {lists.length} lista{lists.length !== 1 ? "s" : ""} · tarifas que aplican en cotizaciones y servicios
                    </div>
                </div>
                {isAdmin && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf,.pdf"
                            onChange={parsePdfWithAi}
                            disabled={aiBusy}
                            className="hidden"
                            data-testid="ai-parse-pdf-input"
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={aiBusy}
                            className="fm-btn-outline disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="ai-parse-pdf-btn"
                            title="Sube un PDF de convenio/contrato y la IA extraerá automáticamente todos los servicios, precios, horas de espera, km y conceptos de cancelación. Te crea la lista lista para usar."
                        >
                            {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
                            <span>{aiBusy ? "Leyendo PDF…" : "Crear desde PDF (IA)"}</span>
                        </button>
                        <button onClick={() => setCreating(true)} className="fm-btn-primary" data-testid="new-list-btn" title="Crea una lista nueva. Puedes empezar de cero o copiar de una existente.">
                            <Plus className="w-3.5 h-3.5" /> Nueva lista
                        </button>
                    </div>
                )}
            </div>

            {creating && (
                <div className="fm-card p-5 border-l-4 border-l-red-500" data-testid="new-list-form">
                    <h3 className="font-display font-bold text-base mb-3">Nueva lista de precios</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <div>
                            <label className="fm-label">Nombre</label>
                            <input className="fm-input" placeholder="Ej. Aseguradoras 2026" value={newList.name} onChange={(e) => setNewList({ ...newList, name: e.target.value })} data-testid="new-list-name" />
                        </div>
                        <div>
                            <label className="fm-label flex items-center gap-1">Copiar de <HelpTip text="Si seleccionas una lista, los precios se copian como punto de partida. Después los ajustas." /></label>
                            <select className="fm-input" value={newList.source_list_id} onChange={(e) => setNewList({ ...newList, source_list_id: e.target.value })} data-testid="new-list-source">
                                <option value="">Empezar desde el catálogo base</option>
                                {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="fm-label">Descripción (opcional)</label>
                            <input className="fm-input" placeholder="Para qué tipo de cliente/contrato es esta lista" value={newList.description} onChange={(e) => setNewList({ ...newList, description: e.target.value })} />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={create} className="fm-btn-primary" data-testid="create-list-confirm-btn">
                            <Save className="w-3.5 h-3.5" /> Crear lista
                        </button>
                        <button onClick={() => setCreating(false)} className="fm-btn-outline">Cancelar</button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Sidebar — listas disponibles */}
                <div className="space-y-2">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Catálogos</div>
                    {lists.map((l) => (
                        <button
                            key={l.id}
                            onClick={() => setActive(l)}
                            className={`w-full text-left p-3 rounded-sm border transition ${active?.id === l.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400"}`}
                            data-testid={`price-list-item-${l.code}`}
                            title={l.description || l.name}
                        >
                            <div className="text-sm font-bold">{l.name}</div>
                            <div className={`text-[10px] font-mono mt-0.5 ${active?.id === l.id ? "text-slate-300" : "text-slate-500"}`}>
                                {l.code}{l.is_default ? " · DEFAULT" : ""}
                            </div>
                        </button>
                    ))}
                </div>

                {/* Editor */}
                <div className="lg:col-span-3">
                    {!active ? (
                        <div className="fm-card p-8 text-center text-slate-500 font-mono text-sm">
                            Selecciona una lista para ver y editar sus precios.
                        </div>
                    ) : (
                        <div className="fm-card p-5">
                            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                                <div className="flex-1 min-w-[280px]">
                                    <label className="fm-label">Nombre de la lista</label>
                                    <input
                                        className="fm-input font-bold text-base"
                                        value={active.name}
                                        disabled={!isAdmin || active.is_default}
                                        onChange={(e) => setActive({ ...active, name: e.target.value })}
                                        data-testid="active-list-name"
                                    />
                                    <textarea
                                        className="fm-input mt-2 text-xs"
                                        rows={2}
                                        placeholder="Descripción"
                                        value={active.description || ""}
                                        disabled={!isAdmin}
                                        onChange={(e) => setActive({ ...active, description: e.target.value })}
                                    />
                                </div>
                                <div className="flex gap-2 self-start">
                                    {isAdmin && (
                                        <button onClick={updateBasic} className="fm-btn-primary" data-testid="save-list-btn" title="Guarda el nombre/descripción de la lista. Los servicios y conceptos se guardan con su propio botón.">
                                            <Save className="w-3.5 h-3.5" /> Guardar
                                        </button>
                                    )}
                                    {isAdmin && !active.is_default && (
                                        <button onClick={() => remove(active)} className="fm-btn-outline text-red-700 border-red-300 hover:bg-red-50" title="Elimina esta lista (no afecta servicios ya cotizados).">
                                            <Trash2 className="w-3.5 h-3.5" /> Eliminar
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Banner contextual (público vs personalizada) */}
                            {active.is_default ? (
                                <div className="mb-3 text-[11px] text-slate-700 bg-blue-50 border-l-4 border-l-blue-400 px-3 py-2.5 space-y-1.5">
                                    <div>
                                        <strong>🏷️ Tarifario Público General — base del sistema.</strong> Estos son los precios que aplican cuando un cliente NO tiene tarifario especial asignado.
                                    </div>
                                    <div className="text-slate-600">
                                        💡 <strong>Recomendación:</strong> mantén este tarifario como tu lista <em>oficial</em>. Para clientes especiales (aseguradoras, hospitales, eventos recurrentes) crea una <strong>"Nueva lista"</strong> arriba y ajústala — esos sí son editables sin riesgo.
                                    </div>
                                    {isAdmin && (
                                        <div className="pt-1">
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    if (!window.confirm("Esto recarga los 22 conceptos oficiales del catálogo de FindMed (los precios que vienen en el PDF original) y SOBRESCRIBE cualquier edición previa en este tarifario.\n\nContinuar?")) return;
                                                    try {
                                                        const { data } = await api.post("/price-lists/seed-public-official");
                                                        toast.success(`Catálogo oficial recargado · ${data.items_loaded} conceptos.`);
                                                        load();
                                                    } catch (e) {
                                                        toast.error(formatApiError(e.response?.data?.detail));
                                                    }
                                                }}
                                                className="text-[11px] underline text-blue-700 hover:text-blue-900 font-bold"
                                                data-testid="reseed-public-btn"
                                            >
                                                ↺ Restaurar catálogo oficial al estado original (PDF de FindMed)
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="mb-3 text-[11px] text-slate-700 bg-emerald-50 border-l-4 border-l-emerald-500 px-3 py-2.5 flex items-start justify-between gap-3 flex-wrap">
                                    <div>
                                        🪄 <strong>Lista personalizada.</strong> Agrega los servicios exactos que maneja este convenio en la sección de abajo. Si quieres partir del catálogo oficial y solo ajustar precios, usa el botón "Copiar tarifario oficial".
                                    </div>
                                    {isAdmin && (
                                        <button
                                            type="button"
                                            onClick={() => copyOfficial(active)}
                                            className="fm-btn-outline text-[11px] whitespace-nowrap"
                                            data-testid="copy-official-btn"
                                            title="Copia los 22 conceptos del Tarifario Público a esta lista. Solo agrega los que no existan (no sobrescribe los que ya tengas)."
                                        >
                                            <Copy className="w-3 h-3" /> Copiar tarifario oficial
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Servicios personalizados de esta lista (oficial + libres). Único editor que importa. */}
                            <CustomServiceTypesEditor list={active} isAdmin={isAdmin} onChange={load} openHistory={openHistory} />

                            {/* Reglas de precio del tarifario (Fase 2) */}
                            <PricingRulesEditor list={active} isAdmin={isAdmin} onChange={load} />

                            {/* Conceptos manuales / extras configurables */}
                            <CustomConceptsEditor list={active} isAdmin={isAdmin} onChange={load} openHistory={openHistory} />
                        </div>
                    )}
                </div>
            </div>

            {historyModal && active && (
                <ConceptHistoryModal
                    listId={active.id}
                    type={historyModal.type}
                    conceptKey={historyModal.key}
                    label={historyModal.label}
                    onClose={() => setHistoryModal(null)}
                />
            )}
        </div>
    );
}

function CustomConceptsEditor({ list, isAdmin, onChange, openHistory }) {
    const [concepts, setConcepts] = useState(list.custom_concepts || []);
    useEffect(() => { setConcepts(list.custom_concepts || []); }, [list.id, list.custom_concepts]);

    const addConcept = () => setConcepts([...concepts, { _tempKey: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, label: "", amount: 0, unit: "MXN", calculation: "fixed", notes: "" }]);
    const updateConcept = (i, k, v) => setConcepts(concepts.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
    const removeConcept = (i) => setConcepts(concepts.filter((_, idx) => idx !== i));
    const saveConcepts = async () => {
        try {
            const { data } = await api.patch(`/price-lists/${list.id}`, { custom_concepts: concepts });
            setConcepts(data.custom_concepts || []);
            toast.success(`Conceptos guardados: ${(data.custom_concepts || []).length}`);
            onChange(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="mt-5 pt-5 border-t-2 border-dashed border-slate-300">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h4 className="font-display font-bold text-base flex items-center gap-2">
                    <ListChecks className="w-4 h-4 text-amber-600" /> Conceptos manuales
                    <HelpTip text="Conceptos adicionales que no están en el catálogo de tipos de servicio: hora de espera extra, % cargo por valoración sin traslado, oxígeno extra, escena complicada, cancelación, etc. Cabina los puede agregar al cotizar." />
                </h4>
                {isAdmin && (
                    <div className="flex gap-2">
                        <button onClick={addConcept} className="fm-btn-outline text-xs" data-testid="add-concept-btn">
                            <Plus className="w-3 h-3" /> Agregar concepto
                        </button>
                        <button onClick={saveConcepts} className="fm-btn-accent text-xs" data-testid="save-concepts-btn">
                            <Save className="w-3 h-3" /> Guardar
                        </button>
                    </div>
                )}
            </div>
            {concepts.length === 0 ? (
                <div className="text-xs text-slate-400 italic py-3 text-center">Sin conceptos manuales. Agrega los que necesites: hora de espera, valoración sin traslado, etc.</div>
            ) : (
                <table className="w-full text-sm">
                    <thead><tr className="border-b border-slate-200 bg-slate-50">
                        {["Concepto", "Monto", "Cálculo", "Unidad", "Notas", ""].map(h => <th key={h} className="text-left py-2 px-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">{h}</th>)}
                    </tr></thead>
                    <tbody>
                        {concepts.map((c, i) => (
                            <tr key={c.id || c._tempKey || `idx-${i}`} className="border-b border-slate-100">
                                <td className="py-1 px-2"><input value={c.label} onChange={(e) => updateConcept(i, "label", e.target.value)} disabled={!isAdmin} className="fm-input text-xs" placeholder="Ej: Hora de espera" autoComplete="off" /></td>
                                <td className="py-1 px-2"><input type="number" step="0.01" value={c.amount} onChange={(e) => updateConcept(i, "amount", parseFloat(e.target.value) || 0)} disabled={!isAdmin} className="fm-input text-xs w-24 text-right font-mono" /></td>
                                <td className="py-1 px-2">
                                    <select value={c.calculation} onChange={(e) => updateConcept(i, "calculation", e.target.value)} disabled={!isAdmin} className="fm-input text-xs">
                                        <option value="fixed">Monto fijo</option>
                                        <option value="percent_of_base">% de tarifa base</option>
                                        <option value="per_hour">Por hora</option>
                                        <option value="per_km">Por km</option>
                                    </select>
                                </td>
                                <td className="py-1 px-2">
                                    <select value={c.unit} onChange={(e) => updateConcept(i, "unit", e.target.value)} disabled={!isAdmin} className="fm-input text-xs">
                                        <option value="MXN">MXN</option>
                                        <option value="%">%</option>
                                        <option value="hora">hora</option>
                                        <option value="km">km</option>
                                    </select>
                                </td>
                                <td className="py-1 px-2"><input value={c.notes || ""} onChange={(e) => updateConcept(i, "notes", e.target.value)} disabled={!isAdmin} className="fm-input text-xs" placeholder="Opcional" /></td>
                                <td>
                                    <div className="flex items-center gap-2">
                                        {openHistory && c.id && (
                                            <button
                                                type="button"
                                                onClick={() => openHistory("concept", c.id, c.label || "concepto")}
                                                className="text-slate-400 hover:text-indigo-600"
                                                title="Ver historial de cambios de este concepto"
                                                data-testid={`concept-history-${i}`}
                                            >
                                                <History className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                        {isAdmin && <button onClick={() => removeConcept(i)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            <div className="text-[10px] text-slate-500 mt-2">
                💡 <strong>Ejemplos:</strong> "Hora de espera adicional" $350 MXN/hora · "Valoración sin traslado" 80% de tarifa base · "Escena contaminada" $500 fijo · "Atención múltiples pacientes" 25% de tarifa base.
            </div>
        </div>
    );
}



function CustomServiceTypesEditor({ list, isAdmin, onChange, openHistory }) {
    const [rows, setRows] = useState(list.custom_service_types || []);
    const [dirty, setDirty] = useState(false);
    useEffect(() => {
        setRows(list.custom_service_types || []);
        setDirty(false);
    }, [list.id, list.custom_service_types]);

    const add = () => {
        setRows([...rows, {
            id: undefined,
            _tempKey: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            code: "",
            name: "",
            base_price: 0,
            round_trip_price: "",
            hour_rate: "",
            per_km: "",
            category: "servicio_base",
            subtype: "",
            unit: "servicio",
            requires_qty: false,
            allow_manual_amount: false,
            notes: "",
            _new: true,
        }]);
        setDirty(true);
    };
    const upd = (i, k, v) => {
        setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
        setDirty(true);
    };
    const del = (i) => {
        const row = rows[i];
        if (row?.name && !window.confirm(`Eliminar "${row.name}" de este tarifario? Esta acción se podrá revertir desde el historial.`)) return;
        setRows(rows.filter((_, idx) => idx !== i));
        setDirty(true);
    };
    const save = async () => {
        // Validación cliente: avisar filas sin nombre
        const incomplete = rows.filter((r) => !(r.name || "").trim());
        if (incomplete.length > 0) {
            if (!window.confirm(`Hay ${incomplete.length} fila(s) sin nombre. Si guardas, se descartarán. Continuar?`)) return;
        }
        try {
            const { data } = await api.patch(`/price-lists/${list.id}`, { custom_service_types: rows });
            // Refresh local rows from server response (the truth) so we SEE what stayed
            const saved = data.custom_service_types || [];
            setRows(saved);
            setDirty(false);
            if (data._warning) {
                toast.warning(data._warning);
            } else {
                toast.success(`Tarifario guardado: ${saved.length} concepto(s) en el catálogo`);
            }
            // Tell parent to refresh `active` too (avoid stale state on re-render)
            onChange(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Error al guardar el tarifario");
        }
    };

    const CATEGORY_OPTIONS = [
        { value: "servicio_base", label: "🚑 Servicio base" },
        { value: "extra_clinico", label: "💉 Extra clínico" },
        { value: "operacion", label: "🛣️ Operación" },
        { value: "evento", label: "🎫 Evento" },
        { value: "renta_guardia", label: "🏠 Renta / Guardia" },
        { value: "sin_categoria", label: "Otros" },
    ];

    return (
        <div className="mt-5 pt-5 border-t-2 border-dashed border-slate-300">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h4 className="font-display font-bold text-base flex items-center gap-2">
                    <FileBadge className="w-4 h-4 text-emerald-600" /> Catálogo de servicios de este tarifario
                    {dirty && <span className="text-[10px] uppercase tracking-widest font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-sm">SIN GUARDAR</span>}
                    <HelpTip text="Catálogo completo de servicios que aplican a este tarifario. Cuando se selecciona esta lista en el Cotizador, estos servicios aparecen agrupados por categoría en el dropdown. Edita precios, redondo, $/hora, $/km — recuerda guardar." />
                </h4>
                {isAdmin && (
                    <div className="flex gap-2">
                        <button onClick={add} className="fm-btn-outline text-xs" data-testid="add-custom-st-btn">
                            <Plus className="w-3 h-3" /> Agregar servicio
                        </button>
                        <button onClick={save} className={`fm-btn-accent text-xs ${dirty ? "ring-2 ring-amber-300" : ""}`} data-testid="save-custom-st-btn">
                            <Save className="w-3 h-3" /> Guardar tarifario
                        </button>
                    </div>
                )}
            </div>
            {rows.length === 0 ? (
                <div className="text-xs text-slate-500 italic py-6 text-center bg-slate-50 border border-dashed border-slate-300 rounded-sm">
                    Sin servicios. Usa <strong>"Agregar servicio"</strong> para capturar uno desde cero, o <strong>"Copiar tarifario oficial"</strong> (arriba) si quieres partir del catálogo público de FindMed.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[1020px]">
                        <thead><tr className="border-b border-slate-200 bg-slate-50">
                            {["Categoría", "Código", "Nombre", "Base", "Redondo", "$/hora", "$/km", "Notas", ""].map(h => <th key={h} className="text-left py-2 px-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">{h}</th>)}
                        </tr></thead>
                        <tbody>
                            {rows.map((s, i) => (
                                <tr key={s.id || s._tempKey || `idx-${i}`} className={`border-b border-slate-100 ${s._new ? "bg-emerald-50/40" : ""}`}>
                                    <td className="py-1 px-2">
                                        <select value={s.category || "sin_categoria"} onChange={(e) => upd(i, "category", e.target.value)} disabled={!isAdmin} className="fm-input text-xs" data-testid={`st-cat-${i}`}>
                                            {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                    </td>
                                    <td className="py-1 px-2">
                                        <input value={s.code} onChange={(e) => upd(i, "code", e.target.value.toUpperCase().replace(/\s+/g, "_"))} disabled={!isAdmin} className="fm-input text-xs font-mono w-24" placeholder="AUTO" data-testid={`st-code-${i}`} />
                                    </td>
                                    <td className="py-1 px-2">
                                        <input value={s.name} onChange={(e) => upd(i, "name", e.target.value)} disabled={!isAdmin} className="fm-input text-xs min-w-[220px]" placeholder="Nombre completo del servicio" data-testid={`st-name-${i}`} />
                                    </td>
                                    <td className="py-1 px-2">
                                        <input type="number" step="50" value={s.base_price ?? 0} onChange={(e) => upd(i, "base_price", parseFloat(e.target.value) || 0)} disabled={!isAdmin} className="fm-input text-xs w-24 text-right font-mono" data-testid={`st-base-${i}`} />
                                    </td>
                                    <td className="py-1 px-2">
                                        <input type="number" step="50" value={s.round_trip_price ?? ""} onChange={(e) => upd(i, "round_trip_price", e.target.value === "" ? "" : (parseFloat(e.target.value) || 0))} disabled={!isAdmin} className="fm-input text-xs w-24 text-right font-mono" placeholder="—" />
                                    </td>
                                    <td className="py-1 px-2">
                                        <input type="number" step="10" value={s.hour_rate ?? ""} onChange={(e) => upd(i, "hour_rate", e.target.value === "" ? "" : (parseFloat(e.target.value) || 0))} disabled={!isAdmin} className="fm-input text-xs w-20 text-right font-mono" placeholder="—" />
                                    </td>
                                    <td className="py-1 px-2">
                                        <input type="number" step="5" value={s.per_km ?? ""} onChange={(e) => upd(i, "per_km", e.target.value === "" ? "" : (parseFloat(e.target.value) || 0))} disabled={!isAdmin} className="fm-input text-xs w-20 text-right font-mono" placeholder="—" />
                                    </td>
                                    <td className="py-1 px-2">
                                        <input value={s.notes || ""} onChange={(e) => upd(i, "notes", e.target.value)} disabled={!isAdmin} className="fm-input text-xs" placeholder="Opcional" />
                                    </td>
                                    <td className="py-1 px-2">
                                        <div className="flex items-center gap-2">
                                            {openHistory && s.code && !s._new && (
                                                <button
                                                    type="button"
                                                    onClick={() => openHistory("service_type", s.code, s.name || s.code)}
                                                    className="text-slate-400 hover:text-indigo-600"
                                                    title="Ver historial de cambios de este servicio"
                                                    data-testid={`st-history-${i}`}
                                                >
                                                    <History className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                            {isAdmin && <button onClick={() => del(i)} className="text-red-500 hover:text-red-700" title="Eliminar de este tarifario"><Trash2 className="w-3.5 h-3.5" /></button>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <div className="text-[10px] text-slate-500 mt-2 leading-snug">
                💡 <strong>Categoría</strong> agrupa los servicios en el dropdown del cotizador (servicios base, extras clínicos, operación, eventos, renta). Si dejas <strong>código</strong> vacío, el sistema lo genera automáticamente del nombre. <strong>Recuerda dar Guardar tarifario</strong> después de tus cambios.
            </div>
        </div>
    );
}


function PricingRulesEditor({ list, isAdmin, onChange }) {
    const defaultRules = {
        wait_block_minutes: 30,
        wait_grace_minutes: 0,
        wait_round_up: true,
        foreign_km_doubles_on_round_trip: false,
        discount_max_pct_without_approval: 20,
    };
    const [rules, setRules] = useState({ ...defaultRules, ...(list.pricing_rules || {}) });
    useEffect(() => { setRules({ ...defaultRules, ...(list.pricing_rules || {}) }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [list.id]);

    const save = async () => {
        try {
            const { data } = await api.patch(`/price-lists/${list.id}`, { pricing_rules: rules });
            toast.success("Reglas guardadas");
            onChange(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="mt-5 pt-5 border-t-2 border-dashed border-slate-300">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h4 className="font-display font-bold text-base flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-600" /> Reglas de precio del tarifario
                    <HelpTip text="Reglas operativas que aplican al cotizador cuando esta lista esté activa: cómo se cobra la espera, qué pasa con km en ida-y-vuelta, y cuánto descuento puede aplicar el operador sin autorización especial." />
                </h4>
                {isAdmin && (
                    <button onClick={save} className="fm-btn-accent text-xs" data-testid="save-pricing-rules">
                        <Save className="w-3 h-3" /> Guardar reglas
                    </button>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50 p-3 rounded-sm">
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Bloque de espera</label>
                    <select disabled={!isAdmin} value={rules.wait_block_minutes} onChange={(e) => setRules({ ...rules, wait_block_minutes: parseInt(e.target.value) })} className="fm-input text-xs" data-testid="rule-wait-block">
                        <option value={15}>15 minutos</option>
                        <option value={30}>30 minutos</option>
                        <option value={60}>60 minutos</option>
                    </select>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Minutos de gracia antes de cobrar espera</label>
                    <input type="number" min="0" disabled={!isAdmin} value={rules.wait_grace_minutes} onChange={(e) => setRules({ ...rules, wait_grace_minutes: parseInt(e.target.value) || 0 })} className="fm-input text-xs" data-testid="rule-wait-grace" />
                </div>
                <div className="flex items-center gap-2">
                    <input type="checkbox" id="rule-wait-roundup" disabled={!isAdmin} checked={rules.wait_round_up} onChange={(e) => setRules({ ...rules, wait_round_up: e.target.checked })} className="w-4 h-4 accent-red-600" data-testid="rule-wait-roundup" />
                    <label htmlFor="rule-wait-roundup" className="text-xs cursor-pointer">Redondear hacia arriba bloques de espera</label>
                </div>
                <div className="flex items-center gap-2">
                    <input type="checkbox" id="rule-km-double" disabled={!isAdmin} checked={rules.foreign_km_doubles_on_round_trip} onChange={(e) => setRules({ ...rules, foreign_km_doubles_on_round_trip: e.target.checked })} className="w-4 h-4 accent-red-600" data-testid="rule-km-double" />
                    <label htmlFor="rule-km-double" className="text-xs cursor-pointer">Duplicar km foráneos en ida y vuelta</label>
                </div>
                <div className="md:col-span-2">
                    <label className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">Descuento máximo sin autorización (%)</label>
                    <input type="number" min="0" max="100" step="1" disabled={!isAdmin} value={rules.discount_max_pct_without_approval} onChange={(e) => setRules({ ...rules, discount_max_pct_without_approval: parseFloat(e.target.value) || 0 })} className="fm-input text-xs" data-testid="rule-discount-max" />
                </div>
                <div className="md:col-span-2 border-t border-dashed border-slate-300 pt-3 mt-1">
                    <div className="text-[10px] uppercase tracking-widest text-slate-700 font-bold mb-2">Overrides por convenio (opcional)</div>
                    <p className="text-[10px] text-slate-500 mb-2">Si este tarifario maneja tarifas distintas a las del catálogo público, captúralas aquí. Si dejas vacío, se usa el precio del catálogo (KM $90, espera $450, MED, PARA_X).</p>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">$/km foráneo</label>
                            <input type="number" min="0" step="5" disabled={!isAdmin} value={rules.per_km_override ?? ""} onChange={(e) => setRules({ ...rules, per_km_override: e.target.value === "" ? null : parseFloat(e.target.value) })} placeholder="default 90" className="fm-input text-xs font-mono" data-testid="rule-per-km-override" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">$/bloque de espera</label>
                            <input type="number" min="0" step="10" disabled={!isAdmin} value={rules.wait_block_price_override ?? ""} onChange={(e) => setRules({ ...rules, wait_block_price_override: e.target.value === "" ? null : parseFloat(e.target.value) })} placeholder="default 450" className="fm-input text-xs font-mono" data-testid="rule-wait-block-override" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">$/h médico a bordo (MED)</label>
                            <input type="number" min="0" step="10" disabled={!isAdmin} value={rules.medic_hourly_rate_override ?? ""} onChange={(e) => setRules({ ...rules, medic_hourly_rate_override: e.target.value === "" ? null : parseFloat(e.target.value) })} placeholder="default catálogo" className="fm-input text-xs font-mono" data-testid="rule-medic-rate-override" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">$/h paramédico extra (PARA_X)</label>
                            <input type="number" min="0" step="10" disabled={!isAdmin} value={rules.paramedic_hourly_rate_override ?? ""} onChange={(e) => setRules({ ...rules, paramedic_hourly_rate_override: e.target.value === "" ? null : parseFloat(e.target.value) })} placeholder="default catálogo" className="fm-input text-xs font-mono" data-testid="rule-paramedic-rate-override" />
                        </div>
                    </div>
                </div>
            </div>
            <div className="text-[10px] text-slate-500 mt-2">
                Ejemplo espera: bloque 30 min, gracia 0 min, redondear → 31 min se cobra como 2 bloques.
                Si activas "duplicar km", al marcar ida-y-vuelta el cotizador multiplica los km por 2 automáticamente.
            </div>
        </div>
    );
}


function ConceptHistoryModal({ listId, type, conceptKey, label, onClose }) {
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/price-lists/${listId}/concept-history`, {
                    params: { concept_type: type, concept_key: conceptKey },
                });
                if (alive) setHistory(data.history || []);
            } catch (e) {
                if (alive) setError(formatApiError(e.response?.data?.detail) || "Error al cargar historial");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [listId, type, conceptKey]);

    const fmtVal = (v) => {
        if (v === null || v === undefined || v === "") return <span className="text-slate-400">—</span>;
        if (typeof v === "number") return <span className="font-mono">{v}</span>;
        if (typeof v === "boolean") return v ? "Sí" : "No";
        return String(v);
    };
    const fmtDate = (iso) => {
        try { return new Date(iso).toLocaleString("es-MX"); } catch { return iso; }
    };

    return (
        <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="history-modal">
            <div className="bg-white rounded-sm shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
                <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5">
                            <History className="w-3.5 h-3.5 text-indigo-600" />
                            Historial de cambios · {type === "service_type" ? "Servicio" : "Concepto"}
                        </div>
                        <div className="font-display font-bold text-base">{label}</div>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="history-modal-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="overflow-y-auto p-5 fm-scroll flex-1">
                    {loading && (
                        <div className="text-center text-slate-500 text-sm py-8">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                            Cargando historial...
                        </div>
                    )}
                    {error && (
                        <div className="text-sm text-red-700 bg-red-50 border-l-4 border-red-400 px-3 py-2">{error}</div>
                    )}
                    {!loading && !error && history.length === 0 && (
                        <div className="text-center text-slate-500 text-sm py-8">
                            Sin historial registrado. Los cambios futuros aparecerán aquí.
                        </div>
                    )}
                    {!loading && history.length > 0 && (
                        <div className="space-y-3">
                            {history.map((h, i) => (
                                <div key={i} className="border border-slate-200 rounded-sm overflow-hidden">
                                    <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-xs">
                                        <div>
                                            <span className="font-bold">{h.user_name}</span>
                                            {h.user_role && <span className="text-slate-500 ml-1">· {h.user_role}</span>}
                                            <span className="text-slate-400 ml-2 font-mono">{h.action.toUpperCase()}</span>
                                        </div>
                                        <div className="text-slate-500 font-mono text-[10px]">{fmtDate(h.at)}</div>
                                    </div>
                                    <div className="p-3 text-xs">
                                        {(!h.changes || h.changes.length === 0) ? (
                                            <div className="text-slate-500 italic">
                                                {h.before && !h.after ? "Eliminado / removido del catálogo." :
                                                 h.after && !h.before ? "Creado por primera vez." :
                                                 "Sin cambios detectables en este concepto."}
                                            </div>
                                        ) : (
                                            <table className="w-full text-xs">
                                                <thead>
                                                    <tr className="text-[9px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                                                        <th className="text-left pb-1">Campo</th>
                                                        <th className="text-left pb-1">Antes</th>
                                                        <th className="text-left pb-1">Después</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {h.changes.map((c, j) => (
                                                        <tr key={j} className="border-b border-slate-100">
                                                            <td className="py-1 font-mono text-[10px] text-slate-600 pr-2">{c.field}</td>
                                                            <td className="py-1 text-slate-500 pr-2 line-through">{fmtVal(c.before)}</td>
                                                            <td className="py-1 text-slate-900 font-bold">{fmtVal(c.after)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="px-5 py-2.5 border-t border-slate-200 bg-slate-50 text-[10px] text-slate-500">
                    Reconstruido a partir del registro de auditoría · {history.length} cambio(s) registrado(s).
                </div>
            </div>
        </div>
    );
}
